import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ContentComponent } from 'Components/ContentComponent/ContentComponent';
import {
    getPlayerPromotionsData,
    getPlayerReport,
    setRoundId,
    setDateRange,
    setRoundTypes,
    setSort,
    setPagination,
    clearPlayerData,
} from 'redux/actions/backoffice/player-report-actions';
import { getGameHistoryReport } from 'redux/actions/backoffice/game-history-actions';
import GameHistory from '../Reports/PlayersReport/GameHistory';
import { currencyUtils, localStorageHelper, getAuthenticatedFetchOptions, getLoginRequestHeader, dateFormatter, Messages } from 'utils';
import { SERVER_URL } from 'utils/config';
import { IPlayerData } from 'redux/reducers/backoffice/player-report-reducer';
import { HeaderTitleComponent } from 'Components/ContentComponent/HeaderTitleComponent';
import { Button, Input, Popover, Table, DatePicker, Dropdown, Checkbox, Tooltip } from 'antd';
import { DownOutlined, DownloadOutlined, SyncOutlined } from '@ant-design/icons';
import type { RangePickerProps } from 'antd/es/date-picker';
import { ErrorComponent } from 'Containers/Errors/ErrorComponent';
import {
    getPlayer,
    getPlayerCampaigns,
    getPlayerCurrencyId,
    getPlayerInternalId,
    getPlayerRawData,
    playerDoesntExist,
    getPlayerPagination,
    getPlayerFilters,
    getRoundId,
    getDateRange,
    getRoundTypes,
    getPaginationParams,
    getSortParams,
    isRoundIdSearchMode,
    hasActiveDateFilter,
    getPlayerLoading,
} from 'redux/selectors/backoffice/player-report-selectors';
import { getGameHistory } from 'redux/selectors/backoffice/game-history-selectors';
import { getServerError } from 'redux/selectors/backoffice/error-selectors';
import { getSearchResult, getSearchStatus, getSearchString } from 'redux/selectors/backoffice/search-selectors';
import { searchPlayers } from 'redux/actions/backoffice/search-actions';
import OPAGameHistory from '../Reports/PlayersReport/OPAGameHistory';
import { Link, useParams, useLocation } from 'react-router-dom';
import { PlayerReportColumns } from './PlayerProfileColumns';
import { getAllGameConfigurationsByUser } from '../../redux/actions/user-actions';
import { useURLSync } from './hooks/useURLSync';
import dayjs from 'dayjs';
import { getAreGameConfigurationsLoading } from '../../redux/selectors/user-selectors';
import { PageNotFoundComponent } from 'Containers/Errors/PageNotFoundComponent';
import { canAccessPlayerProfile } from 'utils';

const PlayerProfileComponent: React.FC = () => {
    const dispatch = useDispatch();
    const { login, playerId } = useParams();
    const { t } = useTranslation();
    
    const playerData = useSelector(getPlayer);
    const noPlayer = useSelector(playerDoesntExist);
    const playerInternalId = useSelector(getPlayerInternalId);
    const playerRawData = useSelector(getPlayerRawData);
    const campaigns = useSelector(getPlayerCampaigns);
    const currencyId = useSelector(getPlayerCurrencyId);
    const paginationData = useSelector(getPlayerPagination);
    const searchResult = useSelector(getSearchResult);
    const isSearching = useSelector(getSearchStatus);
    const searchString = useSelector(getSearchString);
    
    const filters = useSelector(getPlayerFilters);
    const roundId = useSelector(getRoundId);
    const dateRange = useSelector(getDateRange);
    const roundTypes = useSelector(getRoundTypes);
    const paginationParams = useSelector(getPaginationParams);
    const sortParams = useSelector(getSortParams);
    
    const isRoundIdMode = useSelector(isRoundIdSearchMode);
    const hasDates = useSelector(hasActiveDateFilter);
    const isLoading = useSelector(getPlayerLoading);
    
    const [resolvedPlayerId, setResolvedPlayerId] = useState<string | null>(null);
    const [chosenPlayer, setChosenPlayer] = useState<IPlayerData | null>(null);
    const [tableHeight, setTableHeight] = useState(0);
    const location = useLocation();
    const urlSyncComplete = useURLSync();
    const hasUrlParams = location.search.length > 0;
    const searchDispatchedRef = useRef<string | null>(null);
    const previousPlayerKeyRef = useRef<string | null>(null);
    const lastRequestRef = useRef<string | null>(null);
    const lastRequestPlayerKeyRef = useRef<string | null>(null);
    const searchedLoginRef = useRef<string | null>(null);
    const lastPlayerIdRef = useRef<string | null>(null);
    const lastPlayerIdStorageKey = 'playerProfile.lastPlayerId';
    const skipNextReportRequestRef = useRef(false);
    const lastResetPlayerIdRef = useRef<string | null>(null);
    
    const currencyCode = currencyUtils.getCurrencyDataById(currencyId)?.code;
    const gameHistory = useSelector(getGameHistory);
    const areGameConfigurationsLoading = useSelector(getAreGameConfigurationsLoading);
    const error = useSelector(getServerError);
    const shouldDisplayFreeRounds = localStorageHelper
        .getChainedValue('user.authorities')
        .includes('VIEW_CAMPAIGNS');

    const getGameHistoryData = useCallback(
        (playerData: IPlayerData) => {
            const { playerId, gameSessionId, startId, gameType } = playerData;
            setChosenPlayer(playerData);

            if (['SLOT', 'PMR', 'FAST_GAME'].includes(gameType)) {
                dispatch(getGameHistoryReport({ playerId, gameSessionId, startId }));
            }
        },
        [dispatch],
    );

    const columns = PlayerReportColumns(
        currencyCode,
        getGameHistoryData,
        playerInternalId,
        playerRawData,
        areGameConfigurationsLoading,
    );
    const totalWidth = columns.reduce((total, col) => total + (col.width || 0), 0);

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight * 0.6;
        setTableHeight(height);
    }, []);

    useEffect(() => {
        const currentPlayerKey = `${playerId || 'null'}_${login || ''}`;
        
        if (previousPlayerKeyRef.current !== null && previousPlayerKeyRef.current !== currentPlayerKey) {
            dispatch(clearPlayerData());
            setResolvedPlayerId(null);
            searchDispatchedRef.current = null;
            searchedLoginRef.current = null;
            lastRequestRef.current = null;
            lastRequestPlayerKeyRef.current = null;
        }
        
        previousPlayerKeyRef.current = currentPlayerKey;
        
        return () => {
            dispatch(clearPlayerData());
        };
    }, [playerId, login, dispatch]);

    useEffect(() => {
        const actualPlayerId = playerId === 'null' ? resolvedPlayerId : playerId;
        const currentPlayerKey = `${playerId || 'null'}_${login || ''}`;
        
        if (!actualPlayerId || actualPlayerId === 'null') {
            return;
        }

        if (playerId === 'null' && (isSearching || !resolvedPlayerId)) {
            return;
        }

        if (hasUrlParams && !urlSyncComplete) {
            return;
        }

        if (lastRequestPlayerKeyRef.current !== currentPlayerKey) {
            lastRequestRef.current = null;
            lastRequestPlayerKeyRef.current = currentPlayerKey;
        }

        const storedLastPlayerId = localStorageHelper.getChainedValue(lastPlayerIdStorageKey) as string | null;
        const lastPlayerId = lastPlayerIdRef.current || storedLastPlayerId;
        if (lastPlayerId && lastPlayerId !== actualPlayerId && lastResetPlayerIdRef.current !== actualPlayerId) {
            return;
        }

        if (skipNextReportRequestRef.current) {
            skipNextReportRequestRef.current = false;
            return;
        }

        const requestKey = JSON.stringify({
            playerId: actualPlayerId,
            login,
            roundId: filters.roundId,
            fromDate: filters.fromDate?.toISOString(),
            toDate: filters.toDate?.toISOString(),
            roundTypes: filters.roundTypes,
            currentPage: filters.currentPage,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
        });

        if (lastRequestRef.current !== requestKey) {
            lastRequestRef.current = requestKey;
            dispatch(getPlayerReport({ 
                playerId: actualPlayerId,
                login 
            }));
        }
    }, [
        resolvedPlayerId,
        playerId,
        login,
        isSearching,
        filters.roundId,
        filters.fromDate,
        filters.toDate,
        filters.roundTypes,
        filters.currentPage,
        filters.pageSize,
        filters.sortBy,
        filters.sortOrder,
        dispatch,
        hasUrlParams,
        urlSyncComplete,
    ]);

    useEffect(() => {
        if (shouldDisplayFreeRounds && !isRoundIdMode && hasDates && dateRange.fromDate && dateRange.toDate) {
            dispatch(getPlayerPromotionsData({
                fromDate: dateRange.fromDate.toDate(), 
                toDate: dateRange.toDate.toDate(), 
                login 
            }));
        }
    }, [dateRange.fromDate, dateRange.toDate, isRoundIdMode, hasDates, login, shouldDisplayFreeRounds, dispatch]);

    const handleRoundIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        dispatch(setRoundId(value || null));
    }, [dispatch]);

    const handleRoundIdSearch = useCallback(() => {
        const actualPlayerId = playerId === 'null' ? resolvedPlayerId : playerId;
        if (actualPlayerId) {
            dispatch(getPlayerReport({ playerId: actualPlayerId, login }));
        }
    }, [dispatch, playerId, resolvedPlayerId, login]);

    const handleDateRangeChange = useCallback((dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            dispatch(setDateRange(dates[0], dates[1]));
        } else {
            dispatch(setDateRange(null, null));
        }
    }, [dispatch]);

    const handleRoundTypesChange = useCallback((value: string[]) => {
        if (value.length > 0) {
            dispatch(setRoundTypes(value));
            localStorageHelper.setChainedValue('user.reports.roundTypes', value);
        }
    }, [dispatch]);

    const roundTypeOptions = [
        { label: 'Regular Rounds', value: 'regular' },
        { label: 'Free Rounds', value: 'free-round' },
        { label: 'Buy Feature', value: 'buy' },
        { label: 'Awarded Feature', value: 'awarded-feature' },
    ];

    const roundTypesDropdownContent = (
        <div
            style={{
                padding: '8px 12px',
                backgroundColor: '#fff',
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
            }}
        >
            <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                value={roundTypes}
                onChange={handleRoundTypesChange}
            >
                    {roundTypeOptions.map((opt) => (
                        <Checkbox 
                            key={opt.value} 
                            value={opt.value}
                            disabled={roundTypes.length === 1 && roundTypes.includes(opt.value)}
                        >
                            {opt.label}
                        </Checkbox>
                    ))}
            </Checkbox.Group>
        </div>
    );

    const datePresets: RangePickerProps['presets'] = [
        {
            label: t('today'),
            value: () => [dayjs(), dayjs()]
        },
        {
            label: t('yesterday'),
            value: () => [dayjs().subtract(1, 'd'), dayjs().subtract(1, 'd')]
        },
        {
            label: t('week_to_date'),
            value: () => [dayjs().startOf('week'), dayjs()]
        },
        {
            label: t('month_to_date'),
            value: () => [dayjs().startOf('month'), dayjs()]
        },
        {
            label: t('last_7_days'),
            value: () => [dayjs().subtract(7, 'd'), dayjs().subtract(1, 'd')]
        },
        {
            label: t('last_30_days'),
            value: () => [dayjs().subtract(30, 'd'), dayjs().subtract(1, 'd')]
        },
        {
            label: t('last_180_days'),
            value: () => [dayjs().subtract(180, 'd'), dayjs().subtract(1, 'd')]
        },
    ];

    const disabledDate = useCallback((current: dayjs.Dayjs) => {
        return current && current > dayjs().endOf('day');
    }, []);

    const handlePageSizeChange = useCallback((current: any, size: any) => {
        dispatch(setPagination(1, size));
    }, [dispatch]);

    const handlePageChange = useCallback((page: number) => {
        dispatch(setPagination(page));
    }, [dispatch]);

    const handleTableChange = useCallback((pagination: any, tableFilters: any, sorter: any) => {
        if (sorter && sorter.field) {
            dispatch(setSort(
                sorter.field,
                sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING'
            ));
        }
    }, [dispatch]);

    const handleExportCSV = useCallback(async () => {
        const actualPlayerId = playerId === 'null' ? resolvedPlayerId : playerId;
        
        if (!actualPlayerId || actualPlayerId === 'null') {
            Messages.error('Player ID is required for export');
            return;
        }

        const requestBody: any = {
            playerId: String(actualPlayerId),
            roundTypes: roundTypes || localStorageHelper.getChainedValue('user.reports.roundTypes') || ['regular', 'buy', 'free-round', 'awarded-feature'],
            sortBy: sortParams?.sortBy || filters?.sortBy || 'startId',
            sortOrder: sortParams?.sortOrder || filters?.sortOrder || 'DESCENDING',
        };

        if (currencyId) {
            requestBody.currencyId = currencyId;
        }

        if (roundId && roundId.trim()) {
            requestBody.roundId = roundId.trim();
        }

        if (!roundId && dateRange.fromDate && dateRange.toDate) {
            requestBody.start = dateFormatter.getFormattedStartDate(dateRange.fromDate.toDate());
            requestBody.end = dateFormatter.getFormattedEndDate(dateRange.toDate.toDate());
        }

        try {
            const response = await fetch(`${SERVER_URL}/game-history/list-with-round-types/csv-export`, getAuthenticatedFetchOptions({
                method: 'POST',
                headers: {
                    ...getLoginRequestHeader('application/json'),
                },
                body: JSON.stringify(requestBody),
            }));

            if (!response.ok) {
                let errorMessage = 'Failed to export data';

                try {
                    const errorData = await response.json();
                    if (errorData.error?.message) {
                        errorMessage = errorData.error.message;
                    } else {
                        errorMessage = JSON.stringify(errorData);
                    }
                } catch (jsonError) {
                    errorMessage = await response.text();
                }

                throw new Error(errorMessage);
            }

            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'player-profile-export.csv';
            if (contentDisposition && contentDisposition.includes('filename=')) {
                filename = contentDisposition.split('filename=')[1].trim();
                filename = filename.replace(/^"|"$/g, '');
            } else {
                const dateStr = dateRange.fromDate && dateRange.toDate
                    ? `${dateRange.fromDate.format('DD-MM-YYYY')}-${dateRange.toDate.format('DD-MM-YYYY')}`
                    : dayjs().format('DD-MM-YYYY');
                filename = `player-profile-${login || actualPlayerId}-${dateStr}.csv`;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            Messages.error('Error exporting data: ' + error.message);
        }
    }, [playerId, resolvedPlayerId, dateRange, roundId, roundTypes, login, sortParams, filters, currencyId]);

    const handleRefresh = useCallback(() => {
        const actualPlayerId = playerId === 'null' ? resolvedPlayerId : playerId;
        if (!actualPlayerId || actualPlayerId === 'null') {
            return;
        }
        lastRequestRef.current = null;
        dispatch(getPlayerReport({ playerId: actualPlayerId, login }));
    }, [dispatch, playerId, resolvedPlayerId, login]);

    const closeGameHistory = useCallback(() => {
        setChosenPlayer(null);
    }, []);

    const chooseGameHistory = useCallback(() => {
        if (!gameHistory || !gameHistory.length || !chosenPlayer) return null;

        return ['SLOT', 'PMR', 'FAST_GAME'].includes(chosenPlayer.gameType) ? (
            <GameHistory
                data={gameHistory}
                summaryData={chosenPlayer}
                currencyId={currencyId}
                onClose={closeGameHistory}
                t={t}
                error={error}
                player={chosenPlayer}
            />
        ) : chosenPlayer.gameType === 'MINES' ? (
            <OPAGameHistory
                roundId={chosenPlayer?.roundId}
                onClose={closeGameHistory}
                t={t}
                error={error}
            />
        ) : null;
    }, [gameHistory, chosenPlayer, currencyId, t, error, closeGameHistory]);

    const reportData = playerData || [];

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    useEffect(() => {
        if (playerId === 'null' && login && !resolvedPlayerId && !isSearching) {
            if (searchDispatchedRef.current !== login) {
                searchDispatchedRef.current = login;
                searchedLoginRef.current = login;
                dispatch(searchPlayers({ searchString: login }));
            }
        }
        
        if (playerId !== 'null' || resolvedPlayerId) {
            searchDispatchedRef.current = null;
        }
    }, [playerId, login, resolvedPlayerId, isSearching, dispatch]);

    useEffect(() => {
        if (playerId === 'null' && searchResult && !isSearching) {
            if (searchedLoginRef.current !== login) {
                return;
            }
            
            if (searchString && searchString !== login) {
                return;
            }
            
            if (searchResult.content && searchResult.content.length > 0) {
                const matchingPlayer = searchResult.content.find(
                    (p: any) => p.externalId === login || p.login === login
                );
                
                if (matchingPlayer && matchingPlayer.id) {
                    const playerMatches = matchingPlayer.externalId === login || matchingPlayer.login === login;
                    
                    if (playerMatches) {
                        const newResolvedPlayerId = String(matchingPlayer.id);
                        setResolvedPlayerId(newResolvedPlayerId);
                        lastRequestRef.current = null;
                    }
                }
            }
        }
    }, [playerId, searchResult, login, isSearching, searchString]);

    useEffect(() => {
        if (playerId !== 'null') {
            setResolvedPlayerId(null);
        }
    }, [playerId]);

    useEffect(() => {
        dispatch(getAllGameConfigurationsByUser());
    }, [dispatch]);

    useEffect(() => {
        const actualPlayerId = playerId === 'null' ? resolvedPlayerId : playerId;
        if (!actualPlayerId) {
            return;
        }
        if (hasUrlParams && !urlSyncComplete) {
            return;
        }

        const storedLastPlayerId = localStorageHelper.getChainedValue(lastPlayerIdStorageKey) as string | null;
        const lastPlayerId = lastPlayerIdRef.current || storedLastPlayerId;
        if (lastPlayerId && lastPlayerId !== actualPlayerId) {
            const today = dayjs();
            skipNextReportRequestRef.current = true;
            lastResetPlayerIdRef.current = actualPlayerId;
            dispatch(setDateRange(today.startOf('day'), today.endOf('day')));
        } else {
            lastResetPlayerIdRef.current = actualPlayerId;
        }
        lastPlayerIdRef.current = actualPlayerId;
        localStorageHelper.setChainedValue(lastPlayerIdStorageKey, actualPlayerId);
    }, [playerId, resolvedPlayerId, dispatch, hasUrlParams, urlSyncComplete]);

    if (!canAccessPlayerProfile()) {
        return <PageNotFoundComponent />;
    }

    return (
        <ContentComponent
            header={
                <div className="flex justify-between w-full mb-3"> 
                    <HeaderTitleComponent title={login} customBreadcrumbs={<div>{t('player')}</div>} />
                    
                    <div className="flex gap-2 items-end">
                        <Tooltip title={t('refresh')}>
                            <Button
                                icon={<SyncOutlined />}
                                onClick={handleRefresh}
                                style={{ height: '32px' }}
                                disabled={!playerId && !resolvedPlayerId}
                            />
                        </Tooltip>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportCSV}
                            style={{ height: '32px' }}
                            disabled={!playerId && !resolvedPlayerId}
                        >
                            Export CSV
                        </Button>
                        <Dropdown
                            overlay={roundTypesDropdownContent}
                            trigger={['click']}
                            placement="bottomLeft"
                        >
                            <Button style={{ height: '32px' }} icon={<DownOutlined />}>
                                Round Types
                            </Button>
                        </Dropdown>
                        <DatePicker.RangePicker
                            value={dateRange.fromDate && dateRange.toDate ? [dateRange.fromDate, dateRange.toDate] : null}
                            onChange={handleDateRangeChange}
                            placeholder={['From Date', 'To Date']}
                            style={{ height: '32px' }}
                            presets={datePresets}
                            allowClear={false}
                            format="DD/MM/YYYY"
                            separator="-"
                            disabledDate={disabledDate}
                            disabled={isRoundIdMode}
                        />
                    </div>
                </div>
            }
            preHeader={
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        alignItems: 'center'
                    }}>
                        {(campaigns && campaigns?.awardedFeature.campaigns.length > 0) ||
                        (campaigns && campaigns?.freeRounds.campaigns.length > 0) ? (
                            <Popover
                                placement="right"
                                content={
                                    <>
                                        {campaigns.freeRounds.campaigns.length > 0 && (
                                            <label className="font-bold">Free Rounds:</label>
                                        )}
                                        <ol>
                                            {campaigns.freeRounds.campaigns.map((campaign: any) => (
                                                <li key={campaign.campaignId}>
                                                    <Link
                                                        to={`/casino/promotions/${campaign.campaignId}`}
                                                    >
                                                        {`${t('promotionId')} ${campaign.campaignId} - (${campaign.playedRounds}/${campaign.assignedRounds})`}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ol>
                                        {campaigns.awardedFeature.campaigns.length > 0 && (
                                            <label className="font-bold">Awarded Feature:</label>
                                        )}
                                        <ol>
                                            {campaigns.awardedFeature.campaigns.map((campaign: any) => (
                                                <li key={campaign.campaignId}>
                                                    <Link
                                                        to={`/casino/awarded-promotion/${campaign.campaignId}`}
                                                    >
                                                        {`${t('promotionId')} ${campaign.campaignId} - (${campaign.playedRounds}/${campaign.assignedRounds})`}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ol>
                                    </>
                                }
                            >
                                <Button>
                                    {`Promotion Rounds ${campaigns.awardedFeature.playedRounds + campaigns.freeRounds.playedRounds}/${campaigns.awardedFeature.assignedRounds + campaigns.freeRounds.assignedRounds}`}
                                </Button>
                            </Popover>
                        ) : null}
                        
                        <Input
                            placeholder="Search by round ID"
                            value={roundId || ''}
                            onChange={handleRoundIdChange}
                            onPressEnter={handleRoundIdSearch}
                            style={{ flex: 1, height: '32px' }}
                            allowClear
                        />
                    </div>
                </div>
            }
            innerContent={
                <>
                    <div className="casino_performance-table-container" style={{ backgroundColor: 'white', borderRadius: '6px', overflow: 'hidden' }}>
                        <Table
                            style={{
                                minWidth: '100%',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                backgroundColor: 'white',
                            }}
                            className={'casino_performance-table'}
                            dataSource={reportData || []}
                            scroll={{ x: totalWidth, y: tableHeight }}
                            columns={columns}
                            onChange={handleTableChange}
                            loading={isLoading}
                            locale={{
                                emptyText: noPlayer ? t('player_is_not_yet_exists') : t('no_data')
                            }}
                            pagination={{
                                current: paginationData?.currentPage || paginationParams.currentPage,
                                total: paginationData?.total || 0,
                                pageSize: paginationParams.pageSize,
                                position: ['bottomRight'],
                                pageSizeOptions: [10, 20, 50, 100],
                                onShowSizeChange: handlePageSizeChange,
                                onChange: handlePageChange,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                style: {
                                    backgroundColor: 'white',
                                    padding: '16px',
                                    borderTop: '1px solid #e5e7eb',
                                    margin: 0,
                                    borderRadius: '0 0 6px 6px',
                                }
                            }}
                            rowKey={(record) => `${record.roundId.toString()}`}
                        />
                    </div>
                    {error && <ErrorComponent error={error} />}
                    {chooseGameHistory()}
                </>
            }
            reportKey={'playerProfile'}
        />
    );
};

export default PlayerProfileComponent;
